// Public supplier registration form (P2.4, screen 21; 01-SPEC §6; 03-SECURITY "Public supplier
// form"). One flat answer set, saved as a draft any time and checked in full on submit. The same
// checks run in the browser (progress and messages) and in the broker (the decision).

import { MAX_UPLOAD_BYTES } from '../../shared/files';
import { z } from 'zod';

export const COUNTRIES = ['SA', 'AE', 'EG', 'OTHER'] as const;
export const SUPPLY_TYPES = ['goods', 'services', 'both'] as const;
export const LEAD_TIMES = [
  'sameDay',
  'days1to3',
  'week1',
  'weeks1to2',
  'weeks2to4',
  'month1',
] as const;
export const DELIVERY_AREAS = ['riyadh', 'jeddah', 'eastern', 'allKsa', 'uae', 'egypt'] as const;
export const PAYMENT_TERMS = ['advance', 'partAdvance', 'onDelivery', 'net30'] as const;
export const DOC_SLOTS = [
  'cr',
  'vat',
  'nationalAddress',
  'profile',
  'priceList',
  'bankLetter',
] as const;
export type DocSlot = (typeof DOC_SLOTS)[number];

/** Code of conduct version the supplier accepts (open item 14: DRAFT until Legal sends it). */
export const CONDUCT_VERSION = 'DRAFT-2026-10';
export const MAX_DOC_BYTES = MAX_UPLOAD_BYTES;

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max, 'too_long')
    .regex(/^[^<>]*$/, 'format');
const choice = <T extends readonly [string, ...string[]]>(values: T) =>
  z.union([z.literal(''), z.enum(values)]);
const many = <T extends readonly [string, ...string[]]>(values: T) =>
  z.array(z.enum(values)).max(values.length);

/** Shape and size limits only: a draft may be incomplete. */
export const answersSchema = z.strictObject({
  legalNameEN: text(200).default(''),
  legalNameAR: text(200).default(''),
  tradingName: text(200).default(''),
  country: choice(COUNTRIES).default(''),
  city: text(100).default(''),
  address: text(300).default(''),
  website: text(200).default(''),
  crNumber: text(30).default(''),
  crExpiry: z.union([z.literal(''), z.iso.date('format')]).default(''),
  /** No longer asked: every supplier gives a VAT number (D108). Kept so older drafts still load. */
  vatRegistered: choice(['yes', 'no'] as const).default(''),
  vatNumber: text(30).default(''),
  contactName: text(120).default(''),
  contactRole: text(120).default(''),
  contactEmail: text(120).default(''),
  contactMobile: text(30).default(''),
  contactWhatsApp: text(30).default(''),
  financeEmail: text(120).default(''),
  supplyType: choice(SUPPLY_TYPES).default(''),
  categories: z
    .array(z.string().regex(/^[A-Z0-9][A-Z0-9-]{1,29}$/))
    .max(40)
    .default([]),
  description: text(1000).default(''),
  brands: text(300).default(''),
  leadTime: choice(LEAD_TIMES).default(''),
  deliveryAreas: many(DELIVERY_AREAS).default([]),
  paymentTerms: many(PAYMENT_TERMS).default([]),
  bankName: text(120).default(''),
  accountHolder: text(200).default(''),
  iban: text(42).default(''),
  swift: text(15).default(''),
  related: choice(['no', 'yes'] as const).default(''),
  relatedDetails: text(500).default(''),
  accurate: z.boolean().default(false),
  pdpl: z.boolean().default(false),
  conduct: z.boolean().default(false),
});
export type Answers = z.infer<typeof answersSchema>;
export type AnswerKey = keyof Answers;

export const emptyAnswers = (): Answers => answersSchema.parse({});

export interface StoredDoc {
  name: string;
  size: number;
}
export type DocFiles = Partial<Record<DocSlot, StoredDoc>>;

export const SECTIONS = [
  {
    id: 'company',
    fields: ['legalNameEN', 'legalNameAR', 'tradingName', 'country', 'city', 'address', 'website'],
  },
  { id: 'registration', fields: ['crNumber', 'crExpiry', 'vatNumber'] },
  {
    id: 'contacts',
    fields: [
      'contactName',
      'contactRole',
      'contactEmail',
      'contactMobile',
      'contactWhatsApp',
      'financeEmail',
    ],
  },
  {
    id: 'supply',
    fields: [
      'supplyType',
      'categories',
      'description',
      'brands',
      'leadTime',
      'deliveryAreas',
      'paymentTerms',
    ],
  },
  {
    id: 'documents',
    fields: [
      'doc.cr',
      'doc.vat',
      'doc.nationalAddress',
      'doc.profile',
      'doc.priceList',
      'doc.bankLetter',
      'bankName',
      'accountHolder',
      'iban',
      'swift',
    ],
  },
  {
    id: 'declarations',
    fields: ['related', 'relatedDetails', 'accurate', 'pdpl', 'conduct'],
  },
] as const satisfies readonly { id: string; fields: readonly (AnswerKey | `doc.${DocSlot}`)[] }[];
export type SectionId = (typeof SECTIONS)[number]['id'];

/** Upper case without spaces or dashes, as banks print IBANs and SWIFT codes. */
export const compact = (v: string) => v.replace(/[\s-]+/g, '').toUpperCase();

/** ISO 13616 check: move the first four characters to the end, letters to numbers, mod 97 = 1. */
export function ibanValid(raw: string): boolean {
  const iban = compact(raw);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  const moved = iban.slice(4) + iban.slice(0, 4);
  let rest = 0;
  for (const ch of moved) {
    const n = /\d/.test(ch) ? ch : String(ch.charCodeAt(0) - 55);
    for (const d of n) rest = (rest * 10 + Number(d)) % 97;
  }
  return rest === 1;
}

/** Last four characters only (bank details are masked outside Procurement and Finance). */
export const maskIban = (raw: string) => {
  const iban = compact(raw);
  return iban ? `${iban.slice(0, 2)}•• •••• ${iban.slice(-4)}` : '';
};

const EMAIL = /^[^\s@<>"]+@[^\s@<>"]+\.[^\s@<>"]+$/;
const PHONE = /^(\+|00)[1-9]\d{6,14}$/;
const SWIFT = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/;
const phone = (v: string) => v.replace(/[\s()-]+/g, '');

/** The format a VAT / TRN / tax number must have in the supplier's country. */
function vatOk(country: Answers['country'], raw: string): boolean {
  const v = raw.replace(/\s+/g, '');
  if (country === 'SA') return /^3\d{13}3$/.test(v);
  if (country === 'AE') return /^\d{15}$/.test(v);
  if (country === 'EG') return /^\d{9}$/.test(v);
  return /^[A-Za-z0-9]{5,20}$/.test(v);
}

/**
 * What the CR box keeps while typing (D108): for a Saudi supplier (or before the country is
 * picked) digits only, at most 10; elsewhere as typed.
 */
export function crInput(country: Answers['country'], raw: string): string {
  return country === 'SA' || country === '' ? raw.replace(/\D+/g, '').slice(0, 10) : raw;
}

/** The National Address certificate (Saudi Post) is required from Saudi suppliers (D108). */
export const needsNationalAddress = (country: Answers['country']) => country === 'SA';

function crOk(country: Answers['country'], raw: string): boolean {
  const v = raw.replace(/\s+/g, '');
  return country === 'SA' ? /^\d{10}$/.test(v) : /^[A-Za-z0-9/-]{3,30}$/.test(v);
}

export type Problem =
  | 'required'
  | 'format'
  | 'email'
  | 'phone'
  | 'cr'
  | 'vat'
  | 'iban'
  | 'ibanSa'
  | 'swift'
  | 'expired'
  | 'tick'
  | 'category';

/**
 * Everything that stops a submit, by field (`doc.<slot>` for documents). `today` is YYYY-MM-DD in
 * Riyadh. `categoryIds`, when given, are the active categories a choice must come from.
 */
export function registrationProblems(
  a: Answers,
  files: DocFiles,
  today: string,
  categoryIds?: ReadonlySet<string>,
): Partial<Record<AnswerKey | `doc.${DocSlot}`, Problem>> {
  const p: Partial<Record<AnswerKey | `doc.${DocSlot}`, Problem>> = {};
  const need = (k: AnswerKey) => {
    const v = a[k];
    if ((typeof v === 'string' && !v.trim()) || (Array.isArray(v) && v.length === 0)) {
      p[k] = 'required';
      return false;
    }
    return true;
  };

  if (need('legalNameEN') && a.legalNameEN.length < 2) p.legalNameEN = 'format';
  need('country');
  need('city');

  if (need('crNumber') && !crOk(a.country, a.crNumber)) p.crNumber = 'cr';
  // Saudi CRs no longer carry an expiry date (Commercial Register Law, 2025), so it is optional.
  if (a.crExpiry && a.crExpiry <= today) p.crExpiry = 'expired';
  // VAT is required from every supplier (D108).
  if (need('vatNumber') && !vatOk(a.country, a.vatNumber)) p.vatNumber = 'vat';

  need('contactName');
  if (need('contactEmail') && !EMAIL.test(a.contactEmail)) p.contactEmail = 'email';
  if (need('contactMobile') && !PHONE.test(phone(a.contactMobile))) p.contactMobile = 'phone';
  if (a.contactWhatsApp && !PHONE.test(phone(a.contactWhatsApp))) p.contactWhatsApp = 'phone';
  if (a.financeEmail && !EMAIL.test(a.financeEmail)) p.financeEmail = 'email';

  need('supplyType');
  if (need('categories') && categoryIds && a.categories.some((c) => !categoryIds.has(c))) {
    p.categories = 'category';
  }
  need('description');
  need('leadTime');
  need('deliveryAreas');
  need('paymentTerms');

  if (!files.cr) p['doc.cr'] = 'required';
  if (!files.vat) p['doc.vat'] = 'required';
  if (needsNationalAddress(a.country) && !files.nationalAddress) {
    p['doc.nationalAddress'] = 'required';
  }
  if (!files.bankLetter) p['doc.bankLetter'] = 'required';
  need('bankName');
  need('accountHolder');
  if (need('iban')) {
    if (!ibanValid(a.iban)) p.iban = 'iban';
    else if (a.country === 'SA' && !/^SA\d{22}$/.test(compact(a.iban))) p.iban = 'ibanSa';
  }
  if (a.country !== 'SA' && need('swift') && !SWIFT.test(compact(a.swift))) p.swift = 'swift';
  if (a.country === 'SA' && a.swift && !SWIFT.test(compact(a.swift))) p.swift = 'swift';

  if (need('related') && a.related === 'yes') need('relatedDetails');
  for (const k of ['accurate', 'pdpl', 'conduct'] as const) if (!a[k]) p[k] = 'tick';
  return p;
}

/** How many problems each section still has (drives the progress panel). */
export function sectionProgress(
  problems: Partial<Record<string, Problem>>,
): Record<SectionId, number> {
  const out = {} as Record<SectionId, number>;
  for (const s of SECTIONS) {
    out[s.id] = (s.fields as readonly string[]).filter((f) => problems[f]).length;
  }
  return out;
}

export const MAX_ANSWERS_BYTES = 16_384;

export const draftBodySchema = z.strictObject({ answers: answersSchema });
export const submitBodySchema = z.strictObject({
  answers: answersSchema,
  /** Cloudflare Turnstile response from the widget; single use, checked by the broker. */
  turnstile: z.string().min(1).max(4096),
});

export interface RegisterCategory {
  id: string;
  nameEN: string;
  nameAR: string;
  type: string;
}

export interface RegisterOpenBody {
  company: string;
  /** Procurement's message when they asked for more information (P2.5); else ''. */
  note: string;
  expiresAt: string;
  answers: Answers;
  files: DocFiles;
  categories: RegisterCategory[];
  /** Public Turnstile site key for the widget. */
  siteKey: string;
}

export interface RegisterSavedBody {
  savedAt: string;
}

export interface RegisterFileBody {
  slot: DocSlot;
  file: StoredDoc;
}

export interface RegisterSubmittedBody {
  supplierId: string;
  submittedAt: string;
}
